import React from 'react';
import {DisplayObjectList} from '../../components/DisplayObjectList';
import {ButtonWithTitle} from '../../components/ButtonWithTitle';
import {Select} from '../../components/Select';
import {SliderWithTitle} from '../../components/SliderWithTitle';
import {
    loadFileDialog,
    readFilesAsArrayBuffer,
    saveObjectToFile
} from '../../helpers';
import {CheatPluginView} from '../../CheatPluginView';
import {CheatSoundTool} from './CheatSoundTool';

type CheatSoundToolState = {
    selectedSound: string;
    selectedSoundVolume: number;
    selectedSoundPanning: number;
};

export class CheatSoundToolView extends CheatPluginView<CheatSoundTool, {}, CheatSoundToolState> {
    public state: CheatSoundToolState = {
        selectedSound: '',
        selectedSoundVolume: 0,
        selectedSoundPanning: 0
    };

    public componentDidMount(): void {
        this.controller.store.on('loaded', () => this.forceUpdate(), this);
    }

    public componentWillUnmount(): void {
        this.controller.store.off('loaded', this);
    }

    protected loadSounds(): void {
        loadFileDialog('audio/*', true)
            .then(readFilesAsArrayBuffer)
            .then( (files) => {
                this.controller.replaceSounds(files);
            });
    }

    protected onSelectSound(selectedSound: string): void {
        const {panning = 0, volume = 1} = this.controller.getSoundConfig(selectedSound);
        const newState = {
            selectedSound,
            selectedSoundPanning: panning * 100,
            selectedSoundVolume: volume * 100
        };

        this.setState(newState);
    }

    protected playSelectedSound(): void {
        this.controller.playSound(this.state.selectedSound);
    }

    protected stopSelectedSound(): void {
        this.controller.stopSound(this.state.selectedSound);
    }

    protected loadSelectedSound(): void {
        loadFileDialog('audio/*')
            .then(readFilesAsArrayBuffer)
            .then( (files) => {
                this.controller.replaceSound(this.state.selectedSound, Array.from(files.values())[0]);
            });
    }

    protected changeVolumeSelectedSound(selectedSoundVolume: number): void {
        this.setState({selectedSoundVolume}, () => {
            this.controller.editSoundConfig(this.state.selectedSound, 'volume', selectedSoundVolume / 100);
        });
    }

    protected changePanningSelectedSound(selectedSoundPanning: number): void {
        this.setState({selectedSoundPanning}, () => {
            this.controller.editSoundConfig(this.state.selectedSound, 'panning', selectedSoundPanning / 100);
        });
    }

    protected saveConfig(): void {
        const config = this.controller.getSoundsConfig();

        saveObjectToFile(config, 'soundsProperties.json');
    }

    public render(): JSX.Element {
        const {selectedSound, selectedSoundVolume, selectedSoundPanning} = this.state;
        const {soundsFiles} = this.controller;

        if (!this.controller.isLoaded()) {
            return <></>;
        }

        return (
            <DisplayObjectList y={80} direction={'column'} gapBetweenChildren={20} sortableChildren={true}>
                <DisplayObjectList direction={'row'} gapBetweenChildren={20}>
                    <ButtonWithTitle
                        onClick={() => this.controller.stopAllSounds()}
                        title={'Stop All Sounds'} fontSize={50} height={200} width={'auto'}
                    />
                    <ButtonWithTitle onClick={this.loadSounds.bind(this)} title={'Load sounds'} fontSize={50} height={200} width={'auto'}/>
                    <ButtonWithTitle
                        onClick={this.saveConfig.bind(this)}
                        title={'Save sounds properties to json'} fontSize={50} height={200} width={'auto'}
                    />
                </DisplayObjectList>
                <Select zIndex={3} selectLength={10} placeholder={'Select soundManager...'}
                    width={1200} value={selectedSound} options={soundsFiles} onChange={this.onSelectSound.bind(this)}
                />
                <DisplayObjectList zIndex={2} visible={!!selectedSound} gapBetweenChildren={20} direction={'column'}>
                    <DisplayObjectList gapBetweenChildren={20} direction={'row'}>
                        <ButtonWithTitle onClick={this.playSelectedSound.bind(this)} title={'Play'} fontSize={50} height={200} width={300}/>
                        <ButtonWithTitle onClick={this.stopSelectedSound.bind(this)} title={'Stop'} fontSize={50} height={200} width={300}/>
                        <ButtonWithTitle onClick={this.loadSelectedSound.bind(this)}
                            title={'Load and replace'} fontSize={50} height={200} width={300}
                        />
                    </DisplayObjectList>
                    <SliderWithTitle
                        title={'Volume'}
                        width={1000} height={20} min={0} max={100} step={0.1} value={selectedSoundVolume}
                        onChange={this.changeVolumeSelectedSound.bind(this)}
                    />
                    <SliderWithTitle
                        title={'Panning'}
                        width={1000} height={20} min={-100} max={100} step={1} value={selectedSoundPanning}
                        onChange={this.changePanningSelectedSound.bind(this)}
                    />
                </DisplayObjectList>
            </DisplayObjectList>
        );
    }
}
